import React from "react";
import styled from "@emotion/styled";
import TeamStats from "./components/TeamStats";
import MainChart from "./components/MainChart/MainChart";
import ClosuresPieChart from "../../../../components/PieCharts/ClosuresPieChart";
import TeamQuarterlyBreakdown from "./components/TeamQuarterlyBreakdown";
import RefinancesPieChart from "./components/RefinancesPieChart";

const RefinancesOverview = () => {

  return (
    <RefinancesPage>
      <RefinancesWorkspace>
        <WorkspaceHeader>
          <div>
            <WorkspaceEyebrow>Portfolio Analytics</WorkspaceEyebrow>
            <WorkspaceTitle>Refinances Overview</WorkspaceTitle>
          </div>
          <WorkspaceSummary>
            Review refinance closure trends, profit contribution, quarterly performance, and portfolio closure distribution.
          </WorkspaceSummary>
        </WorkspaceHeader>

        <WorkspaceBody>
          <SummaryRail>
            <RailHeader>
              <RailTitle>Distribution Summary</RailTitle>
              <RailSummary>Closure mix, refinance source, and quarterly profit comparison.</RailSummary>
            </RailHeader>
            <RailStack>
              <ClosuresPieChart />
              <RefinancesPieChart />
              <TeamQuarterlyBreakdown />
            </RailStack>
          </SummaryRail>

          <AnalysisColumn>
            <MainChart />
            <TeamStats />
          </AnalysisColumn>
        </WorkspaceBody>
      </RefinancesWorkspace>
    </RefinancesPage>
  )
}

const RefinancesPage = styled.main(({ theme }) => `
  width: 100%;
  height: 100%;
  min-height: 0;
  display: grid;
  padding: 0.75rem;
  overflow: hidden;
  background: ${theme.palette.common.pageBackground};
`)

const RefinancesWorkspace = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.9rem;
  background: ${theme.palette.common.cardBackground};
  box-shadow: inset 0 0.1rem 0 rgba(255, 255, 255, 0.72);
`)

const WorkspaceHeader = styled.header(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.3rem;
  padding: 0.85rem 1.15rem 0.75rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.white};
`)

const WorkspaceEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.15rem;
  color: ${theme.palette.primary.main};
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  text-transform: uppercase;
`)

const WorkspaceTitle = styled.h1(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 1.42rem;
  font-weight: 800;
  line-height: 1.15;
`)

const WorkspaceSummary = styled.p(({ theme }) => `
  max-width: 64rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.86rem;
  font-weight: 550;
  line-height: 1.32;
`)

const WorkspaceBody = styled.div(({ theme }) => `
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(15rem, 17.5rem) minmax(0, 1fr);
  gap: 0.75rem;
  padding: 0.75rem;
  overflow-x: hidden;
  overflow-y: hidden;
  background: ${theme.palette.common.pageBackground};
`)

const SummaryRail = styled.aside(({ theme }) => `
  min-width: 0;
  min-height: 0;
  align-self: stretch;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
`)

const RailHeader = styled.header(({ theme }) => `
  min-width: 0;
  display: grid;
  gap: 0.12rem;
  padding: 0.55rem 0.7rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const RailTitle = styled.h2(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 0.88rem;
  font-weight: 800;
  line-height: 1.2;
`)

const RailSummary = styled.p(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.7rem;
  font-weight: 650;
  line-height: 1.25;
`)

const RailStack = styled.div`
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.08fr);
  gap: 0.45rem;
  padding: 0.5rem;
  overflow: hidden;
`

const AnalysisColumn = styled.section`
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(22rem, 1fr) auto;
  gap: 0.75rem;
`

export default RefinancesOverview;
